import * as React from "react";
import { Box, Chip, Grid, Stack, Typography } from "@mui/material";

import { languageMatches } from "@/lib/languages";
import type { CampaignRequestAgent, CampaignRequestDetail } from "@/hooks/api/use-campaign-requests";
import { useUpdateCampaignRequestAgent } from "@/hooks/api/use-campaign-requests";
import type { CatalogueVoice } from "@/hooks/api/use-voices";
import { useVoiceCatalogue } from "@/hooks/api/use-voices";

import { DocumentList, MarketScheduleCard } from "../components";
import { formatDateTime, formatWallClock, isFrozen, SYSTEM_DISPOSITION_LINE } from "../constants";
import type { VoiceChoice } from "../create/voice-picker";
import { VoicePicker } from "../create/voice-picker";
import { describeTemplate } from "../notification-template";
import { agentForMarketCard, agentHeading, agentKindTag, agentLanguageLine } from "./agent-naming";
import { EmptyState, Facts, Field, Muted, Panel } from "./parts";

/**
 * Everything the campaign was set up to do: who answers and what they sound like, where and when it
 * dials, what a call is recorded as, what we pull out of it, who hears about a win, and what we say
 * on every call.
 *
 * Read-mostly rather than read-only. Two things a client comes back to change after the fact are
 * the voice an agent speaks in and the files it works from, and neither was reachable from here —
 * the only way to swap a voice was to re-enter the wizard. Both now sit on the thing they belong
 * to, and both close the same way the API closes: a confirmed request is settled, so the picker
 * goes read-only and says why rather than presenting a dead control.
 *
 * The sweep this tab exists to survive: an agent is three agents, so it is a card set and not a
 * table; its kind is a pill of one or two words rather than the wizard's whole sentence; the
 * markets an agent answers are printed once, on the market rows, rather than on both sides of the
 * same relationship; a notification template is read out in the words a person chose — never as
 * the `{{contact__sales_rep}}` the mail path stores; and an empty panel keeps its share of the row
 * instead of collapsing and dragging its neighbour out of line.
 */

export interface ConfigurationTabProps {
	request: CampaignRequestDetail;
}

/** How long a "none of these voices" note is left alone before it is written. */
const NOTE_SETTLE_MS = 1200;

export function ConfigurationTab({ request }: ConfigurationTabProps): React.JSX.Element {
	const agentsById = React.useMemo(() => new Map(request.agents.map((agent) => [agent.id, agent])), [request.agents]);
	const documentsById = React.useMemo(
		() => new Map(request.documents.map((document) => [document.id, document])),
		[request.documents]
	);

	// The same catalogue the wizard reads, in one call for every language on the request; the
	// per-language narrowing is `languageMatches`, so `en` still answers an `en-SG` agent.
	const { data: voices = [], isLoading: voicesLoading, isError: voicesFailed } = useVoiceCatalogue();

	const settled = isFrozen(request.state);

	const transfer =
		request.transferNumber === null
			? "Not offered"
			: `${request.transferNumber}${
					request.transferWindowStart || request.transferWindowEnd
						? ` · ${formatWallClock(request.transferWindowStart)} – ${formatWallClock(request.transferWindowEnd)}`
						: ""
				}`;

	// The client's own words for their own fields, so a subject built on one reads as its title
	// rather than as the variable the extraction writes.
	const captureLabels = React.useMemo(
		() => Object.fromEntries(request.captureFields.map((field) => [field.appearsAs, field.title])),
		[request.captureFields]
	);

	const describe = React.useCallback(
		(template: string | null | undefined): string => describeTemplate(template ?? "", captureLabels),
		[captureLabels]
	);

	const notification = request.notification;
	const to = describe(notification?.toTemplate);
	const bcc = describe(notification?.bccTemplate);
	const subject = describe(notification?.subjectTemplate);

	// An empty To line sends nothing, and nothing visibly fails when it does — so it is the one
	// thing that decides whether this panel has anything to report.
	const emails = to !== "";

	const recipients: React.JSX.Element[] = [];

	if (emails && notification) {
		recipients.push(
			<Field key="to" label="To" value={to} />,
			...(bcc === "" ? [] : [<Field key="bcc" label="BCC" value={bcc} />]),
			...(subject === "" ? [] : [<Field key="subject" label="Subject" value={subject} />]),
			<Field key="fallback" label="Lead with no owner" value={notification.fallbackEmail ?? "Nothing is sent"} />,
			...(notification.clientNote ? [<Field key="note" label="Your note" value={notification.clientNote} />] : [])
		);
	}

	if (request.stakeholderEmails.length > 0) {
		recipients.push(<Field key="informed" label="Kept informed" value={request.stakeholderEmails.join(" · ")} />);
	}

	return (
		<Stack spacing={2}>
			<Panel title="Agents">
				{request.agents.length === 0 ? (
					<EmptyState>No agents chosen yet.</EmptyState>
				) : (
					<Stack spacing={1.5}>
						{request.agents.map((agent) => {
							const kind = agentKindTag(agent.kind);
							const language = agentLanguageLine(agent);
							const flow = agent.callFlowDocumentId ? documentsById.get(agent.callFlowDocumentId) : undefined;

							return (
								<Box key={agent.id} sx={{ border: "1px solid var(--mui-palette-divider)", borderRadius: 1, p: 1.5 }}>
									<Stack direction="row" spacing={1} sx={{ alignItems: "flex-start", justifyContent: "space-between" }}>
										<Box sx={{ minWidth: 0 }}>
											<Typography variant="subtitle2">{agentHeading(agent)}</Typography>
											{language ? (
												<Typography variant="caption" color="text.secondary">
													{language}
												</Typography>
											) : null}
										</Box>

										{kind ? <Chip color="primary" label={kind} size="small" variant="soft" /> : null}
									</Stack>

									{/* The file itself, named, rather than a pill saying one exists. */}
									{agent.callFlowDocumentId ? (
										<Typography
											variant="caption"
											color="text.secondary"
											sx={{ display: "block", mt: 0.5, wordBreak: "break-all" }}
										>
											Call flow · {flow ? flow.originalFilename : "attached"}
										</Typography>
									) : null}

									<Box sx={{ mt: 1.25 }}>
										<AgentVoice
											requestId={request.id}
											agent={agent}
											voices={voices}
											loading={voicesLoading}
											failed={voicesFailed}
											readOnly={settled}
										/>
									</Box>
								</Box>
							);
						})}

						{/*
						 * Said once, under the set, rather than beside every silent picker. The API is
						 * the reason and it is worth being honest about: a settled request takes client
						 * writes no longer, so a control that looked live here would fail on use.
						 */}
						{settled ? (
							<Muted>Voices are settled once the quote is confirmed. Ask us and we&rsquo;ll swap one.</Muted>
						) : null}
					</Stack>
				)}
			</Panel>

			{/*
			 * The two files a campaign is actually rebuilt from, on the screen where a client is
			 * reading what the campaign does. The Documents tab keeps the whole record — the
			 * statement of work, the purchase order, everything ever attached; this is the same
			 * shared list narrowed to the kinds that belong to a configuration decision, so a
			 * replacement list or a reworked script can be sent from the thing it changes.
			 *
			 * Uploading stays open on a settled request. Sending a fresh lead list after
			 * confirmation is ordinary; only removing what is already on the record closes.
			 */}
			<DocumentList
				requestId={request.id}
				documents={request.documents}
				kinds={["lead_list", "call_flow", "revised_call_flow"]}
				filterKinds={["lead_list", "call_flow", "revised_call_flow"]}
				title="Lead lists and call flows"
				emptyText="Nothing sent yet."
				allowDelete={!settled}
			/>

			<Panel title="Markets and schedule">
				{request.marketSchedules.length === 0 ? (
					<EmptyState>No markets scheduled yet.</EmptyState>
				) : (
					<Stack spacing={2}>
						{request.marketSchedules.map((market) => {
							const agent = market.agentId ? agentsById.get(market.agentId) : null;

							return (
								<MarketScheduleCard key={market.id} market={market} agent={agent ? agentForMarketCard(agent) : null} />
							);
						})}
					</Stack>
				)}
			</Panel>

			<Grid container spacing={2}>
				<Grid size={{ xs: 12, md: 6 }}>
					<Panel title="Outcomes">
						{request.outcomes.length === 0 ? (
							// With nothing of the client's own, the platform's own list is the answer —
							// printed here instead of under an empty box that would say it twice.
							<EmptyState>Only the standard outcomes: {SYSTEM_DISPOSITION_LINE}.</EmptyState>
						) : (
							<React.Fragment>
								<Stack spacing={1.5}>
									{request.outcomes.map((outcome) => (
										<Box key={outcome.id}>
											<Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap", gap: 1 }}>
												<Typography variant="body2" sx={{ fontWeight: 600 }}>
													{outcome.name}
												</Typography>
												{outcome.isConversion ? (
													<Chip color="success" label="Conversion" size="small" variant="soft" />
												) : null}
												{outcome.notifyOnMatch ? (
													<Chip color="info" label="Emails your team" size="small" variant="soft" />
												) : null}
											</Stack>
											<Typography variant="caption" color="text.secondary">
												{outcome.description}
											</Typography>
										</Box>
									))}
								</Stack>

								{/* Ours, not theirs: the dialer writes these on every call whatever the client adds. */}
								<Muted>Also recorded on every call: {SYSTEM_DISPOSITION_LINE}.</Muted>
							</React.Fragment>
						)}
					</Panel>
				</Grid>

				<Grid size={{ xs: 12, md: 6 }}>
					<Panel title="What we capture">
						{request.captureFields.length === 0 ? (
							<EmptyState>Nothing extra — we still record the outcome, the summary and the recording.</EmptyState>
						) : (
							<Stack spacing={1.5}>
								{request.captureFields.map((field) => (
									<Box key={field.id}>
										<Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap", gap: 1 }}>
											<Typography variant="body2" sx={{ fontWeight: 600 }}>
												{field.title}
											</Typography>
											{field.enabled ? null : <Chip label="Off" size="small" variant="outlined" />}
										</Stack>
										{/*
										 * The definition, and nothing under it. What used to sit here was
										 * `appearsAs` — the template key the extraction writes against, which
										 * is ours to keep stable across a rename and never the client's to
										 * read. It is the same leak as a raw variable on the panel below.
										 */}
										<Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
											{field.description}
										</Typography>
									</Box>
								))}
							</Stack>
						)}
					</Panel>
				</Grid>
			</Grid>

			<Grid container spacing={2}>
				<Grid size={{ xs: 12, md: 6 }}>
					<Panel title="Who hears about a win">
						{recipients.length === 0 ? (
							<EmptyState>Nobody is emailed when a call converts.</EmptyState>
						) : (
							<Stack spacing={1.5}>
								<Facts>{recipients}</Facts>

								{/*
								 * Why "Sales rep" is a recipient. The stored template is `{{contact__sales_rep}}`
								 * and that string never reaches this screen — it is a key into a row, and a
								 * client reading who gets their conversions should see the column, not the key.
								 */}
								{emails ? <Muted>Each name is filled in from that lead&rsquo;s own row.</Muted> : null}
							</Stack>
						)}
					</Panel>
				</Grid>

				<Grid size={{ xs: 12, md: 6 }}>
					<Panel title="Compliance">
						<Facts>
							<Field label="Recording disclosed" value={<YesNo value={request.recordingDisclosure} />} />
							<Field label="AI Disclosed" value={<YesNo value={request.aiDisclosure} />} />
							<Field label="Transfer to a person" value={transfer} />
							<Field label="Your suppression list" value={request.dncListDocumentId ? "Applied" : "None supplied"} />
							<Field
								label="Consent to call confirmed"
								value={request.leadConsentConfirmedAt ? formatDateTime(request.leadConsentConfirmedAt) : "—"}
							/>
							<Field
								label="Estimates acknowledged"
								value={request.estimatesAcknowledgedAt ? formatDateTime(request.estimatesAcknowledgedAt) : "—"}
							/>
							{/*
							 * A reference is printed where there is one. Where the paperwork is required and
							 * missing it is not restated here — it is already on the Summary tab, under the
							 * one heading that answers "what do you still need from me".
							 */}
							{request.sowReference ? <Field label="Statement of work" value={request.sowReference} /> : null}
							{request.poReference ? <Field label="Purchase order" value={request.poReference} /> : null}
						</Facts>
					</Panel>
				</Grid>
			</Grid>
		</Stack>
	);
}

function YesNo({ value }: { value: boolean }): React.JSX.Element {
	return value ? (
		<React.Fragment>Yes</React.Fragment>
	) : (
		<Typography component="span" variant="body2" color="warning.main">
			No
		</Typography>
	);
}

interface AgentVoiceProps {
	requestId: string;
	agent: CampaignRequestAgent;
	/** The whole active catalogue; narrowed to this agent's language here. */
	voices: CatalogueVoice[];
	loading: boolean;
	failed: boolean;
	readOnly: boolean;
}

/**
 * One agent's voice, changeable in place.
 *
 * The wizard's own picker, imported rather than re-drawn: the same cards, the same samples, the
 * same "none of these" ask, so a voice chosen after the fact is chosen exactly as it was the first
 * time. What is different here is that there is no draft to carry to a Continue button, so the row
 * is written as it is decided.
 *
 * A voice is a click and is saved on it. The note beside "none of these" is typing, so it is left
 * to settle first — a write per keystroke would be a toast per keystroke and a row rewritten a
 * dozen times for one sentence.
 */
function AgentVoice({ requestId, agent, voices, loading, failed, readOnly }: AgentVoiceProps): React.JSX.Element {
	const updateAgent = useUpdateCampaignRequestAgent();

	const offered = React.useMemo(
		() => voices.filter((voice) => languageMatches(voice.language, agent.language)),
		[voices, agent.language]
	);

	const persisted = React.useMemo<VoiceChoice>(
		() => ({ voiceId: agent.voiceId, requestNote: agent.voiceRequestNote ?? "" }),
		[agent.voiceId, agent.voiceRequestNote]
	);

	const [draft, setDraft] = React.useState<VoiceChoice>(persisted);

	const settling = React.useRef(false);
	const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

	// The saved row is the truth, and it moves under us whenever the request is refetched. The one
	// moment it must not win is while a note is still settling, which would be our own echo
	// overwriting the words being typed.
	React.useEffect(() => {
		if (!settling.current) setDraft(persisted);
	}, [persisted]);

	React.useEffect(
		() => () => {
			if (timer.current) clearTimeout(timer.current);
		},
		[]
	);

	const save = (choice: VoiceChoice): void => {
		settling.current = false;

		updateAgent.mutate({
			requestId,
			agentId: agent.id,
			data: {
				voiceId: choice.voiceId,
				// An empty note is no ask at all, and null is how the API is told the ask is withdrawn.
				voiceRequestNote: choice.requestNote.trim() === "" ? null : choice.requestNote.trim(),
			},
		});
	};

	const handleChange = (choice: VoiceChoice): void => {
		setDraft(choice);

		if (timer.current) {
			clearTimeout(timer.current);
			timer.current = null;
		}

		if (choice.voiceId !== draft.voiceId) {
			save(choice);
			return;
		}

		if (choice.requestNote === persisted.requestNote) return;

		settling.current = true;
		timer.current = setTimeout(() => {
			timer.current = null;
			save(choice);
		}, NOTE_SETTLE_MS);
	};

	return (
		<VoicePicker
			language={agent.language}
			voices={offered}
			loading={loading}
			failed={failed}
			value={draft}
			readOnly={readOnly}
			onChange={handleChange}
		/>
	);
}
